import { useEffect } from "react";

function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const goBack = () => {
    window.history.back();
  };
  
  return (
    <div className="min-h-screen bg-base-100">
      <header className="bg-neutral text-neutral-content py-4 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <h1 
            className="text-3xl font-bold font-grotesk hover:opacity-80 transition-opacity cursor-pointer" 
            onClick={goBack}
          >
            PEAK<span className="font-light text-base-content/60">^</span>
          </h1>
          <button
            onClick={goBack}
            className="text-sm hover:underline"
          >
            Back
          </button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-bold font-grotesk mb-4">
          Terms of Service
        </h1>
        <p className="text-base-content/70 mb-12">
          Effective Date: 14 September 2026
        </p>

        <div className="space-y-8 text-base-content">
          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              1. Introduction and acceptance
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              1.1 About These Terms
            </h3>
            <p className="leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("you" or "User") and PEAK APP PTY LTD (ABN 34 701 745 422), trading as Peak ("Peak", "we", "us", or "our").
            </p>
            <p className="leading-relaxed mb-4">
              These Terms govern your access to and use of the Peak platform, including our websites available at <a href="https://peak.app" className="text-blue-600 hover:underline">https://peak.app</a>, <a href="https://ams.peak.app" className="text-blue-600 hover:underline">https://ams.peak.app</a>, <a href="https://learn.peak.app" className="text-blue-600 hover:underline">https://learn.peak.app</a>, <a href="https://help.peak.app" className="text-blue-600 hover:underline">https://help.peak.app</a>, <a href="https://trust.peak.app" className="text-blue-600 hover:underline">https://trust.peak.app</a>, and <a href="https://academy.peak.app" className="text-blue-600 hover:underline">https://academy.peak.app</a>, and our mobile applications, and all related services, features, content, and functionality (collectively, the "Services").
            </p>
            <p className="leading-relaxed mb-4 font-semibold">
              Important: please read these terms carefully before using peak. By accessing or using our services, you agree to be bound by these terms. If you do not agree to all of these terms, do not use our services.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              1.2 Acceptance of Terms
            </h3>
            <p className="leading-relaxed mb-4">
              By creating an account, accessing, or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. You also agree to comply with our Privacy Policy, available at <a href="/privacy-policy" className="text-blue-600 hover:underline">peak.app/privacy-policy</a>, which is incorporated into these Terms by reference.
            </p>
            <p className="leading-relaxed mb-4">
              If you are using the Services on behalf of a business, organisation, or other entity, you represent and warrant that you have the authority to bind that entity to these Terms, and "you" refers to both you, individually and that entity.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              1.3 Changes to These Terms
            </h3>
            <p className="leading-relaxed">
              We may modify these Terms at any time. When we make material changes, we will notify you by email and/or through a notification within the Services. Your continued use of the Services after such notification constitutes your acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using the Services and may terminate your account in accordance with Section 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              2. Eligibility
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              2.1 Age Requirements
            </h3>
            <p className="leading-relaxed mb-4">
                You must be at least 13 years of age to create an account and access core features of the Services. Access to social features is restricted to users who are 16 years of age or older, consistent with applicable Australian online safety legislation. If you are under 16, you must have obtained your parent or guardian's consent before creating an account. We do not knowingly collect personal information from individuals under 13 years of age.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              2.2 Capacity to Contract
            </h3>
            <p className="leading-relaxed mb-4">
              By agreeing to these Terms, you represent and warrant that you have the legal capacity to enter into a binding contract and are not prohibited from using the Services under the laws of your jurisdiction.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              2.3 Geographic Availability
            </h3>
            <p className="leading-relaxed">
              While Peak is accessible internationally, certain features may not be available in all regions. You are responsible for ensuring your use of the Services complies with all applicable local laws and regulations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              3. Account registration and security
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.1 Account Creation
            </h3>
            <p className="leading-relaxed mb-4">
              To access and use the Services, you must create an account. During registration, you will be required to provide certain information, including your name, email address, phone number, date of birth, address, and gender. You may also be required to provide payment information to access paid features.
            </p>
            <p className="leading-relaxed">
              You may create an account using your email address or by signing in through a third-party service (Facebook, Google, or Apple). If you use a third-party service, you authorise us to access and use certain information from that service as permitted by its terms.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.2 Account Accuracy
            </h3>
            <p className="leading-relaxed">
              You agree to provide accurate, current, and complete information during registration and to update this information to keep it accurate, current, and complete. Providing false or misleading information may result in immediate termination of your account.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.3 Account Security
            </h3>
            <p className="leading-relaxed mb-3">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>keep your password secure and confidential;</li>
              <li>notify us immediately of any unauthorised access to or use of your account; and</li>
              <li>ensure you log out of your account at the end of each session when using shared or public devices.</li>
            </ul>
            <p className="leading-relaxed">
              We are not liable for any loss or damage arising from your failure to protect your account credentials.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              3.4 Multiple Accounts
            </h3>
            <p className="leading-relaxed">
              You may not create or maintain more than one account without our prior written consent. We reserve the right to terminate or merge duplicate accounts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              4. Description of services
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.1 The Peak Platform
            </h3>
            <p className="leading-relaxed">
              Peak is a health and fitness technology platform designed for athletes and coaches. Our Services include training analytics, performance tracking, personalised insights, and AI-powered features to help users optimise their athletic training and performance.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.2 AI-Powered Features
            </h3>
            <p className="leading-relaxed mb-3">
              Peak includes artificial intelligence features that provide personalised insights to athletes and coaches based on training data. These features include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>The Wizard:</strong> An AI-powered chatbot that allows you to interact with and learn more about your training data.</li>
              <li><strong>Personalised Insights:</strong> AI-generated analysis and recommendations based on your training patterns and performance data.</li>
              <li><strong>Research-Based Guidance:</strong> Recommendations informed by curated physiotherapy and sports science research.</li>
            </ul>
            <p className="leading-relaxed">
              Our AI features are powered by third-party services, including Google's Gemini. When you use AI features, relevant portions of your data are processed by these third-party services in accordance with our Privacy Policy.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.3 Third-Party Integrations
            </h3>
            <p className="leading-relaxed">
              The Services integrate with third-party platforms and services, including Terra (for fitness device integration) and Stripe (for payment processing). Your use of these integrations is subject to the terms and privacy policies of those third-party services. We are not responsible for the practices or content of third-party services.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.4 Beta Features
            </h3>
            <p className="leading-relaxed">
              From time to time, we may offer beta, preview, or experimental features ("Beta Features"). Beta Features are provided "as is" and may be modified, suspended, or discontinued at any time without notice. By using Beta Features, you acknowledge that they may contain bugs, errors, or other issues and that your use is at your own risk.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              4.5 Modifications to Services
            </h3>
            <p className="leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any part of the Services at any time, with or without notice. We will endeavour to provide reasonable notice of material changes where practicable. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              5. Subscription and payment
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.1 Subscription Plans
            </h3>
            <p className="leading-relaxed">
              Peak offers both free and paid subscription plans. Free accounts provide access to basic features, while paid subscriptions ("Premium Subscriptions") unlock additional features and functionality. Details of available subscription plans and pricing are displayed on our website and within the app.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.2 Billing and Payment
            </h3>
            <p className="leading-relaxed mb-4">
              Premium Subscriptions are billed on a monthly recurring basis. Payment is processed through our third-party payment processor, Stripe. By subscribing, you authorise us to charge your designated payment method for all applicable fees.
            </p>
            <p className="leading-relaxed">
              All prices displayed are inclusive of GST (where applicable). You are responsible for any additional taxes or fees that may apply based on your location.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.3 Automatic Renewal
            </h3>
            <p className="leading-relaxed">
              Premium Subscriptions automatically renew at the end of each billing period unless you cancel before the renewal date. You will be charged the applicable subscription fee for each renewal period until you cancel.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.4 Price Changes
            </h3>
            <p className="leading-relaxed">
              We may change our subscription prices from time to time. We will provide you with reasonable advance notice of any price changes before they take effect. Price changes will apply to your next billing cycle following the notice period. If you do not agree to the new pricing, you may cancel your subscription before the price change takes effect.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.5 Failed Payments
            </h3>
            <p className="leading-relaxed">
              If a payment fails, we may attempt to process the payment again. If payment continues to fail, we may suspend or restrict your access to Premium features until the outstanding amount is paid. We reserve the right to terminate your account for persistent non-payment.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              5.6 Refunds
            </h3>
            <p className="leading-relaxed mb-4">
              Refunds are provided at our discretion. If you believe you are entitled to a refund, please contact us at <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>. Refund requests will be evaluated on a case-by-case basis, taking into account factors such as the nature of your request, your usage of the Services, and applicable consumer protection laws.
            </p>
            <p className="leading-relaxed">
              This refund policy does not affect your rights under Australian Consumer Law. For more information about your consumer rights, see Section 11.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              6. User content
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              6.1 Your Content
            </h3>
            <p className="leading-relaxed">
              The Services may allow you to submit, upload, post, or otherwise make available content, including text, comments, training data, and other information ("User Content"). You retain ownership of any intellectual property rights you hold in your User Content.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              6.2 Licence Grant
            </h3>
            <p className="leading-relaxed mb-4">
              By submitting User Content to the Services, you grant us a non-exclusive, worldwide, royalty-free, sublicensable, and transferable licence to use, reproduce, modify, adapt, publish, display, distribute, and create derivative works from your User Content for the purpose of operating, providing, improving, and promoting the Services.
            </p>
            <p className="leading-relaxed">
              Where you have provided consent, we may also use your User Content (or portions thereof) for marketing or promotional purposes. You may withdraw this consent at any time by contacting us at <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              6.3 User Content Representations
            </h3>
            <p className="leading-relaxed mb-2">
              You represent and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>you own or have the necessary rights and permissions to submit the User Content and grant us the licence described above;</li>
              <li>your User Content does not infringe, misappropriate, or violate the intellectual property, privacy, or other rights of any third party;</li>
              <li>your User Content complies with these Terms and all applicable laws and regulations; and</li>
              <li>your User Content does not contain any material that is false, misleading, defamatory, obscene, or otherwise objectionable.</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              6.4 Content Moderation
            </h3>
            <p className="leading-relaxed">
              We may, but are not obligated to, monitor, review, or moderate User Content. We reserve the right, at our sole discretion, to remove, edit, or disable access to any User Content for any reason, including if we determine that User Content violates these Terms. We are not responsible for User Content submitted by you or other users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              7. Acceptable use and prohibited conduct
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.1 General Obligations
            </h3>
            <p className="leading-relaxed">
              You agree to use the Services only for lawful purposes and in accordance with these Terms. You are responsible for all activities conducted through your account.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.2 Prohibited Conduct
            </h3>
            <p className="leading-relaxed mb-2">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>use the Services for any illegal, unlawful, or fraudulent purpose;</li>
              <li>harass, abuse, threaten, stalk, or intimidate other users;</li>
              <li>send spam, unsolicited messages, or engage in any form of bulk messaging;</li>
              <li>impersonate any person or entity, or falsely state or misrepresent your affiliation with any person or entity;</li>
              <li>attempt to gain unauthorised access to the Services, other users' accounts, or any systems or networks connected to the Services;</li>
              <li>transmit any viruses, malware, or other malicious code;</li>
              <li>infringe or misappropriate any intellectual property, privacy, or other rights of Peak or third parties;</li>
              <li>upload, post, or transmit any adult, sexually explicit, or pornographic content;</li>
              <li>upload, post, or transmit any content that promotes hatred, discrimination, or violence against any individual or group;</li>
              <li>use any automated means, including bots, scrapers, or spiders, to access or collect data from the Services without our prior written permission;</li>
              <li>use the Services for any commercial purpose without our prior written consent;</li>
              <li>interfere with or disrupt the integrity or performance of the Services;</li>
              <li>reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Services; or</li>
              <li>violate any applicable local, state, national, or international law or regulation.</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              7.3 Consequences of Violation
            </h3>
            <p className="leading-relaxed mb-3">
              If we determine that you have violated these Terms, we may take any action we deem appropriate, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>issuing a warning;</li>
              <li>removing or disabling access to your User Content;</li>
              <li>temporarily suspending your access to the Services;</li>
              <li>permanently terminating your account; and/or</li>
              <li>pursuing legal action against you.</li>
            </ul>
            <p className="leading-relaxed">
              The specific action we take will depend on the nature and severity of the violation. We reserve the right to take any of these actions without prior notice where we believe immediate action is necessary.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              8. Intellectual property
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              8.1 Our Intellectual Property
            </h3>
            <p className="leading-relaxed">
              The Services, including all content, features, functionality, software, text, graphics, logos, trademarks, and other materials (collectively, "Peak Content"), are owned by us or our licensors and are protected by Australian and international copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              8.2 Limited Licence
            </h3>
            <p className="leading-relaxed mb-3">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable licence to access and use the Services for your personal, non-commercial purposes. This licence does not include any right to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>sell, resell, or commercially exploit the Services or Peak Content;</li>
              <li>copy, reproduce, distribute, or publicly display Peak Content;</li>
              <li>modify or create derivative works based on the Services or Peak Content;</li>
              <li>reverse engineer, decompile, or disassemble any software used to provide the Services; or</li>
              <li>remove or alter any proprietary notices or labels on the Services or Peak Content.</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              8.3 Feedback
            </h3>
            <p className="leading-relaxed">
              If you provide us with any feedback, suggestions, or ideas regarding the Services ("Feedback"), you grant us an unrestricted, irrevocable, perpetual, royalty-free right to use, modify, and incorporate such Feedback into the Services without any obligation to compensate you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              9. Health and wellness disclaimers
            </h2>
            <p className="leading-relaxed mb-4 font-semibold">
              Please read this section carefully. It contains important limitations on the nature of our services.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.1 Not Medical Advice
            </h3>
            <p className="leading-relaxed mb-4 font-semibold">
              Peak is not a medical service, and nothing in the Services constitutes or is intended to constitute medical advice, diagnosis, treatment, or professional health guidance. The Services are designed for general fitness and athletic training purposes only.
            </p>
            <p className="leading-relaxed">
              The information, insights, and recommendations provided through the Services, including AI-generated content, are for informational and educational purposes only. They should not be relied upon as a substitute for professional medical advice, diagnosis, or treatment.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.2 Consult Healthcare Professionals
            </h3>
            <p className="leading-relaxed mb-4">
              Before beginning any exercise program or making changes to your training regimen, you should consult with a qualified healthcare professional, particularly if you have any pre-existing health conditions, injuries, or concerns.
            </p>
            <p className="leading-relaxed">
              If you experience any pain, discomfort, or unusual symptoms during or after exercise, stop immediately and seek medical attention.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.3 Emergency Services
            </h3>
            <p className="leading-relaxed font-semibold">
              Peak is not an emergency service. In the event of a medical emergency, call 000 (Australia) or your local emergency services immediately. Do not rely on the Services to respond to or manage emergency situations.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.4 No Guarantee of Results
            </h3>
            <p className="leading-relaxed">
              We make no representations or warranties regarding the effectiveness of the Services or any specific outcomes you may achieve. Individual results will vary based on many factors outside our control, including your physical condition, training history, adherence to recommendations, and other variables.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.5 AI Limitations
            </h3>
            <p className="leading-relaxed">
              The AI-powered features of the Services, including the Wizard chatbot, generate outputs based on algorithms and the data you provide. While we strive to provide helpful and accurate information, AI-generated content may contain errors, omissions, or inaccuracies. You are responsible for evaluating and verifying any information provided by our AI features before relying on it.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              9.6 Assumption of Risk
            </h3>
            <p className="leading-relaxed">
              By using the Services, you acknowledge that physical exercise involves inherent risks, including the risk of injury. You voluntarily assume all risks associated with your use of the Services and any physical activities you undertake based on information obtained through the Services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              10. Disclaimers
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              10.1 "As Is" Provision
            </h3>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, the Services are provided "as is" and "as available" without warranties of any kind, whether express, implied, or statutory, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              10.2 No Uptime Guarantee
            </h3>
            <p className="leading-relaxed">
              We do not guarantee that the Services will be uninterrupted, error-free, secure, or free from viruses or other harmful components. We may perform maintenance, updates, or repairs that may temporarily affect availability.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              10.3 Third-Party Content
            </h3>
            <p className="leading-relaxed">
              The Services may include content from third parties, links to third-party websites, or integrations with third-party services. We do not endorse, guarantee, or assume responsibility for any third-party content, products, or services. Your interactions with third parties are solely between you and the third party.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              10.4 Consumer Law Notice
            </h3>
            <p className="leading-relaxed mb-4 font-semibold">
              Nothing in these terms is intended to exclude, restrict, or modify any consumer rights under the competition and consumer act 2010 (Cth), schedule 2 (Australian Consumer Law), or any other applicable consumer protection legislation that cannot be excluded, restricted, or modified by agreement.
            </p>
            <p className="leading-relaxed">
              If the Australian Consumer Law applies to you as a consumer, our goods and services come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled to a replacement or refund for a major failure and compensation for any other reasonably foreseeable loss or damage. You are also entitled to have the goods repaired or replaced if the goods fail to be of acceptable quality and the failure does not amount to a major failure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              11. Limitation of liability
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.1 Exclusion of Certain Damages
            </h3>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, we are not liable to you for any indirect, incidental, special, consequential, punitive, or exemplary damages, including but not limited to damages for loss of profits, goodwill, data, or other intangible losses, arising out of or in connection with these Terms or your use of (or inability to use) the Services, regardless of the cause of action or the theory of liability, and even if we have been advised of the possibility of such damages.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.2 Cap on Liability
            </h3>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, our total aggregate liability to you for any and all claims arising out of or in connection with these Terms or the Services is limited to the greater of: (a) the amount you paid to us in the 12 months preceding the claim; or (b) AUD $100.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.3 Consumer Law Limitations
            </h3>
            <p className="leading-relaxed mb-3">
              The limitations and exclusions in this Section 11 do not apply to liability that cannot be excluded or limited under applicable law, including the Australian Consumer Law. To the extent our liability cannot be excluded, our liability is limited (at our option) to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>in the case of goods: replacement of the goods or supply of equivalent goods; repair of the goods; payment of the cost of replacing the goods or acquiring equivalent goods; or payment of the cost of having the goods repaired; and</li>
              <li>in the case of services: supplying the services again; or payment of the cost of having the services supplied again.</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              11.4 Essential Basis
            </h3>
            <p className="leading-relaxed">
              You acknowledge that the limitations of liability in this Section 11 reflect a reasonable allocation of risk between us and are an essential basis of the bargain between us. We would not be able to provide the Services to you on an economically viable basis without these limitations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              12. Indemnification
            </h2>
            <p className="leading-relaxed mb-3">
              To the maximum extent permitted by law, you agree to indemnify, defend, and hold harmless Peak and its officers, directors, employees, agents, and affiliates from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or in connection with:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>your use of the Services;</li>
              <li>your User Content;</li>
              <li>your violation of these Terms;</li>
              <li>your violation of any applicable laws or regulations; or</li>
              <li>your infringement or misappropriation of any third-party rights.</li>
            </ul>
            <p className="leading-relaxed">
              This indemnification obligation does not apply to consumers to the extent it would be considered an unfair contract term under Australian Consumer Law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              13. Term and termination
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              13.1 Term
            </h3>
            <p className="leading-relaxed">
              These Terms commence when you first access or use the Services and continue until terminated in accordance with this Section 13.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              13.2 Termination by You
            </h3>
            <p className="leading-relaxed">
              You may terminate your account at any time by following the account deletion process within the Services or by contacting us at <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>. Termination will take effect immediately upon processing your request.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              13.3 Termination by Us
            </h3>
            <p className="leading-relaxed mb-3">
              We may suspend or terminate your account and access to the Services at any time, with or without notice, for any reason, including if we reasonably believe that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>you have breached these Terms;</li>
              <li>you have failed to pay any applicable fees;</li>
              <li>you have engaged in illegal activity;</li>
              <li>your account has been inactive for an extended period; or</li>
              <li>continued provision of the Services to you is no longer commercially viable or would expose us to legal or reputational risk.</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              13.4 Effect of Termination
            </h3>
            <p className="leading-relaxed mb-2">
              Upon termination of your account:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>your right to access and use the Services will immediately cease;</li>
              <li>you may request a copy of your User Content before termination takes effect by contacting us at <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>;</li>
              <li>we may delete your User Content in accordance with our Privacy Policy;</li>
              <li>any rights or obligations that by their nature should survive termination will continue, including Sections 6.2 (Licence Grant), 8 (Intellectual Property), 9 (Health and Wellness Disclaimers), 10 (Disclaimers), 11 (Limitation of Liability), 12 (Indemnification), 14 (Dispute Resolution), and 16 (General Provisions).</li>
            </ul>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              13.5 Refunds Upon Termination
            </h3>
            <p className="leading-relaxed">
              Whether you are entitled to a refund upon termination depends on the circumstances. If we terminate your account without cause, we may provide a pro-rata refund for any prepaid subscription fees. If your account is terminated for breach of these Terms or other cause, you are generally not entitled to a refund. Refund entitlements under Australian Consumer Law are not affected by this Section.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              14. Dispute Resolution
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              14.1 Complaints
            </h3>
            <p className="leading-relaxed">
              If you have a complaint or concern about the Services, please contact us at <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>. We will acknowledge your complaint within a reasonable timeframe and work with you to resolve the issue.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              14.2 Informal Resolution
            </h3>
            <p className="leading-relaxed">
              Before initiating any formal legal proceedings, you agree to first attempt to resolve any dispute informally by contacting us. We will endeavour to resolve disputes through good faith negotiation within 30 days of receiving notice of the dispute.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              14.3 Governing Law
            </h3>
            <p className="leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of Queensland, Australia. Any dispute arising out of or in connection with these Terms will be subject to the exclusive jurisdiction of the courts of Queensland, Australia.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              14.4 Consumer Rights Preserved
            </h3>
            <p className="leading-relaxed">
              Nothing in this Section 14 is intended to limit your rights under applicable consumer protection laws, including your right to bring proceedings in your place of residence or to seek injunctive or other equitable relief.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              15. Privacy
            </h2>
            <p className="leading-relaxed mb-4">
              Your privacy is important to us. Our collection, use, and disclosure of your personal information is governed by our Privacy Policy, available at <a href="https://peak.app/privacy-policy" className="text-blue-600 hover:underline">https://peak.app/privacy-policy</a>. By using the Services, you consent to the collection, use, and disclosure of your information as described in the Privacy Policy.
            </p>
            <p className="leading-relaxed">
              We use cookies and similar tracking technologies as described in our Privacy Policy. By using the Services, you consent to the use of these technologies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              16. General Provisions
            </h2>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.1 Entire Agreement
            </h3>
            <p className="leading-relaxed">
              These Terms, together with the Privacy Policy, constitute the entire agreement between you and Peak regarding the Services and supersede all prior and contemporaneous agreements, understandings, and communications, whether oral or written.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.2 Severability
            </h3>
            <p className="leading-relaxed">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions will continue in full force and effect. The invalid, illegal, or unenforceable provision will be modified to the minimum extent necessary to make it valid, legal, and enforceable while preserving its original intent.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.3 Waiver
            </h3>
            <p className="leading-relaxed">
              Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision. Any waiver of any provision of these Terms will be effective only if in writing and signed by us.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.4 Assignment
            </h3>
            <p className="leading-relaxed">
              You may not assign or transfer these Terms or any rights hereunder without our prior written consent. We may assign or transfer these Terms, in whole or in part, without restriction, including in connection with a merger, acquisition, corporate reorganisation, or sale of assets.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.5 Force Majeure
            </h3>
            <p className="leading-relaxed">
              We are not liable for any failure or delay in performing our obligations under these Terms where such failure or delay results from causes beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, riots, pandemics, government actions, power failures, or failures of third-party service providers.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.6 Notices
            </h3>
            <p className="leading-relaxed mb-4">
              We may provide notices to you via email to the address associated with your account, through in-app notifications, or by posting on the Services. You are responsible for ensuring that your contact information is current.
            </p>
            <p className="leading-relaxed">
              Notices to us should be sent by email to <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a>.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.7 Relationship of Parties
            </h3>
            <p className="leading-relaxed">
              Nothing in these Terms creates a partnership, joint venture, agency, or employment relationship between you and Peak. Neither party has authority to bind the other or incur obligations on the other's behalf.
            </p>

            <h3 className="text-xl font-semibold font-grotesk mb-3 mt-6">
              16.8 Third-Party Rights
            </h3>
            <p className="leading-relaxed">
              These Terms do not confer any rights or remedies upon any person other than you and Peak, except as expressly stated.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold font-grotesk mb-4">
              17. Contact information
            </h2>
            <p className="leading-relaxed mb-4">
              If you have any questions about these Terms or the Services, please contact us:
            </p>
            <div className="bg-base-200 p-4 rounded-lg">
              <p className="mb-1"><strong>Email:</strong> <a href="mailto:support@peak.app" className="text-blue-600 hover:underline">support@peak.app</a></p>
              <p className="mb-1"><strong>Website:</strong> <a href="https://peak.app" className="text-blue-600 hover:underline">https://peak.app</a></p>
              <p className="mb-1"><strong>Entity:</strong> PEAK APP PTY LTD</p>
              <p className="mb-1"><strong>ABN:</strong> 34 701 745 422</p>
              <p><strong>Support Hours:</strong> 9:00 AM - 5:00 PM AEST, Monday to Friday</p>
            </div>
          </section>
        </div>
        
        <div className="mt-12 pt-8 border-t border-base-300">
          <p className="text-sm text-base-content/70 text-center">
            By using Peak, you agree to the terms outlined in these Terms of Service.
          </p>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-base-100 border-t border-base-300">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Logo */}
            <div className="flex flex-col items-center md:items-start gap-4">
              <div className="text-3xl font-grotesk font-bold">
                PEAK<span className="font-light text-base-content/60">^</span>
              </div>
              <p className="text-base-content/70 text-sm">
                © {new Date().getFullYear()} Peak. All rights reserved.
              </p>
            </div>
            
            {/* Links */}
            <div className="flex gap-6 text-sm">
              <a 
                href="/privacy-policy" 
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;